import { useLocalStorage } from "@vueuse/core";
import * as constants from "@/constants";
import type { ArchiveEntry, ArchiveGame, GameTableEntry, LobbyResp, Profile } from "@/server-types";

export async function fetchApi<T = any>(path: string, props?: { body?: object; method?: string }) {
	const body = props?.body ? JSON.stringify(props.body) : undefined;
	const method = props?.method ?? "GET";

	let res;
	const headers: Record<string, string> = {
		"Content-Type": "application/json"
	};

	const auth_token = useLocalStorage("access_token", "").value;
	if (auth_token) {
		headers["Authorization"] = `Bearer ${auth_token}`;
	}

	try {
		res = await fetch(constants.BACKEND_HTTP + path, {
			method,
			headers,
			body
		});
	} catch (error) {
		console.error("API request failed", method, path, error);
		throw new Error("Cannot reach the server. Check your connection and try again.");
	}
	if (res.ok) {
		if (res.status == 204) {
			return { _empty: true } as T;
		}

		const resp_text = await res.text();

		if (import.meta.env.DEV) {
			console.debug(
				"[DEV] HTTP_RESP url:[%s] code:[%s] body:[%s]",
				constants.BACKEND_HTTP + path,
				res.status,
				resp_text
			);
		}

		if (!resp_text || resp_text.length == 0) {
			return { _empty: true } as T;
		}

		try {
			return JSON.parse(resp_text) as T;
		} catch (error) {
			throw new Error("Cannot parse response");
		}

    }

    const fallback: Record<number, string> = {
        400: "The server could not complete that request.",
        401: "Your session has expired. Please sign in again.",
        403: "You do not have permission to do that.",
        404: "The requested player or lobby could not be found.",
        409: "The lobby has changed. Refresh it and try again.",
        500: "The server encountered an error. Please try again.",
        503: "The server is temporarily unavailable. Please try again."
    };
    let errorMessage = fallback[res.status] ?? `Request failed (HTTP ${res.status}). Please try again.`;
    try {
        const parsed = JSON.parse(await res.text());
        if (typeof parsed.error === "string" && parsed.error) errorMessage = parsed.error;
    } catch {
        // A bare HTTP error is valid; preserve its status-specific explanation.
    }
    console.error("API request failed", method, path, res.status);
    throw new Error(errorMessage);
}

interface LoginResp {
	access_token: string;
	refresh_token: string;
	user: Profile;
}

export const login = (username: string, password: string) =>
	fetchApi<LoginResp>("/api/user/login", { method: "POST", body: { username, password } });

export const register = (username: string, email: string, password: string) =>
	fetchApi<LoginResp>("/api/user/register", { method: "POST", body: { username, email, password } });

export const refreshAccessToken = (refresh_token: string) =>
	fetchApi("/api/user/refresh", { method: "POST", body: { refresh_token } });

export const getProfile = (username: string) => fetchApi<Profile>("/api/profile/" + username);

// BEGIN: Lobby
export const getMyLobbyID = () => fetchApi<{ lobbyID: number }>("/api/lobby").then((res) => res.lobbyID);
export const getLobbyByID = (id: number) => fetchApi<LobbyResp>(`/api/lobby/${id}/list`);
export const createLobby = () => fetchApi("/api/lobby/", { method: "PUT" });
export const addBot = (id: number, teamId: "A" | "B", variant?: string) => {
	const qs = variant ? `?variant=${encodeURIComponent(variant)}` : "";
	return fetchApi(`/api/lobby/${id}/add-bot/${teamId}${qs}`);
};
export interface BotVariant {
	name: string;
	displayName: string;
	elo?: number;
	imageUrl?: string;
	ratingNote?: string;
}
/** The bots this server can seat, as a player picks them. Empty when the server offers no choice. */
export const getBotVariants = () =>
	fetchApi<{ variants: BotVariant[] }>("/api/lobby/bots").then((res) => res.variants ?? []);
export const startLobby = (id: number, points: number) => fetchApi(`/api/lobby/${id}/start?points=${points}`);
export const deleteLobby = (id: number) => fetchApi("/api/lobby/" + id, { method: "DELETE" });
export const leaveLobby = (id: number) => fetchApi(`/api/lobby/${id}/leave`);
export const removeLobbyPlayer = (id: number, username: string) =>
	fetchApi(`/api/lobby/${id}/player/${encodeURIComponent(username)}`, { method: "DELETE" });
export const sendLobbyInvitation = (id: number, username: string, side: "teammate" | "opponent") =>
	fetchApi(`/api/lobby/${id}/invite/${side}/${username}`);
export const acceptLobbyInvitation = (id: number) => fetchApi(`/api/lobby/${id}/accept`);
export const listLobbies = () => fetchApi<Record<number, LobbyResp>>(`/api/lobby/list`);
export const joinLobby = (id: number, teamId: "A" | "B") => fetchApi(`/api/lobby/${id}/join/${teamId}`);
// END: Lobby

export const getOnline = () => fetchApi("/api/profile/online");

export const sendFriendRequest = (username: string) => fetchApi(`/api/user/friends/send-request/${username}`);
export const acceptFriendRequest = (username: string) => fetchApi(`/api/user/friends/accept-request/${username}`);
export const rejectFriendRequest = (username: string) => fetchApi(`/api/user/friends/reject-request/${username}`);
export const removeFriend = (username: string) => fetchApi(`/api/user/friends/${username}`, { method: "DELETE" });

export const changePassword = (old_password: string, new_password: string) =>
	fetchApi("/api/user/change-password", {
		method: "POST",
		body: { old_password, new_password }
	});

export const changeProfilePictureUrl = (url: string) =>
	fetchApi("/api/user/change-profile-picture-url", {
		method: "POST",
		body: { url }
	});

export const sendCredentialsFromGoogle = (code: string) =>
	fetchApi("/api/user/login-with-third-party/google", {
		method: "POST",
		body: { code }
	});

export const checkUsername = (username: string) => fetchApi("/api/user/check-username/" + username);
export const selectUsernameGoogle = (username: string, code: string) =>
	fetchApi<LoginResp>("/api/user/login-with-third-party/google/select-username", {
		method: "POST",
		body: { username, code }
	});

// Leaderboards
export const listLeaderboards = () => fetchApi<string[]>("/api/leaderboard/list");
export const getLeaderboard = (name: string) => fetchApi<Array<[string, number]>>("/api/leaderboard/" + name);

// Charts
export const getRatingHistory = (username: string) =>
	fetchApi<[number, number][]>(`/api/profile/${username}/history/elo`);

// Past games
export const getGameData = (id: string) => fetchApi<ArchiveGame>(`/api/games/data/${id}`);
export const getGameInfo = (id: string) => fetchApi<GameTableEntry>(`/api/games/info/${id}`);
export const getRecentGameUUIDs = () => fetchApi<string[]>(`/api/games/recent`);
/** One player's finished games, newest first (at most 50). */
export const getPlayerGames = (username: string, limit = 10) =>
	fetchApi<GameTableEntry[]>(`/api/profile/${encodeURIComponent(username)}/games?limit=${limit}`);

// Live games
export const getLiveGames = () => fetchApi<Omit<GameTableEntry, "game" | "endedTs">[]>(`/api/games/live`);
